import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "primereact/card";
import {
  BillingConstants,
  DEFAULT_BILLING_BRAND_CONFIG,
  SHARED_ONE_TIME_BUNDLE_PACKAGES,
  SHARED_ROLE_ONE_TIME_BUNDLE_PURCHASE_TYPES,
  getSharedPlanComparisonConfigByRole,
  getSharedSubscriptionPlanContentByType,
} from "../../../constants/billing";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER, ROLE_TYPE_FAMILY_MEMBER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import PublicRoleSelector from "../components/PublicRoleSelector";
import {
  publicPageContainerStyle,
  publicSectionCardStyle,
} from "../../../components/general/publicPageStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const sectionTitleStyle = {
  color: "#0f3d63",
  fontSize: "1rem",
  fontWeight: 700,
  marginBottom: "0.75rem",
} as const;

const sectionLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.45rem",
  borderRadius: "999px",
  padding: "0.32rem 0.7rem",
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "0.02em",
  textTransform: "uppercase" as const,
} as const;

const listStyle = {
  color: "#36546c",
  lineHeight: 1.75,
  paddingLeft: "1.2rem",
} as const;

const getPricingRoleTheme = (providerBadge: string): Record<string, any> => ({
  [ROLE_TYPE_PROVIDER]: {
    badge: providerBadge,
    accent: "#0f3d63",
    soft: "#eef6ff",
    border: "#cfe0f1",
    chip: "#e38b2c",
  },
  [ROLE_TYPE_NURSE]: {
    badge: "Nurse",
    accent: "#0b5cab",
    soft: "#f4f9ff",
    border: "#d8e7f7",
    chip: "#0ea5e9",
  },
  [ROLE_TYPE_MHP]: {
    badge: "MHP",
    accent: "#0b5cab",
    soft: "#f4f9ff",
    border: "#d8e7f7",
    chip: "#0ea5e9",
  },
  [ROLE_TYPE_CAREGIVER]: {
    badge: "Staff",
    accent: "#166534",
    soft: "#f3fbf5",
    border: "#cfe8d6",
    chip: "#16a34a",
  },
  [ROLE_TYPE_FACILITY_WORKER]: {
    badge: "Facility Worker",
    accent: "#166534",
    soft: "#f3fbf5",
    border: "#cfe8d6",
    chip: "#16a34a",
  },
  [ROLE_TYPE_FAMILY_MEMBER]: {
    badge: "Family Member",
    accent: "#7c2d12",
    soft: "#fff7ed",
    border: "#f1dbc7",
    chip: "#c76b11",
  },
});

const resolvePricingRole = (roleParam: string | null, availableRoles: string[] = []) => {
  const normalizedRole = String(roleParam || "").trim().toLowerCase();
  const supportedRoles = availableRoles.length > 0
    ? availableRoles
    : [ROLE_TYPE_PROVIDER, ROLE_TYPE_NURSE, ROLE_TYPE_CAREGIVER];
  return supportedRoles.includes(normalizedRole) ? normalizedRole : ROLE_TYPE_PROVIDER;
};

const Pricing = () => {
  const authExperience = useAuthExperience();
  const availableRoleTypes = useMemo(() => authExperience.roleOptions.map((role) => role.value), [authExperience.roleOptions]);
  const pricingRoleTheme = useMemo(
    () => getPricingRoleTheme(authExperience.nurseWorkspace.providerLabel),
    [authExperience.nurseWorkspace.providerLabel],
  );
  const billingBrandConfig = useMemo(() => ({
    ...DEFAULT_BILLING_BRAND_CONFIG,
    brandKey: authExperience.brandKey,
    brandName: authExperience.brandName,
    providerLabel: authExperience.nurseWorkspace.providerLabel,
    providerPluralLabel: authExperience.nurseWorkspace.providerPluralLabel,
    websiteLabel: authExperience.discovery.jobWebsiteLabel,
    vendorCommunityLabel: authExperience.vocabulary.facilityPluralLower,
  }), [
    authExperience.brandKey,
    authExperience.brandName,
    authExperience.discovery.jobWebsiteLabel,
    authExperience.nurseWorkspace.providerLabel,
    authExperience.nurseWorkspace.providerPluralLabel,
    authExperience.vocabulary.facilityPluralLower,
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState<string | null>(() => resolvePricingRole(searchParams.get("role"), availableRoleTypes));
  const [activeSection, setActiveSection] = useState<"subscription" | "oneTime" | "comparison">("subscription");
  const [bundleFilter, setBundleFilter] = useState<string>("all");
  const sharedOneTimeBundleMap = useMemo(
    () => new Map(SHARED_ONE_TIME_BUNDLE_PACKAGES.map((bundle) => [bundle.purchaseType, bundle])),
    []
  );
  const sharedSubscriptionPlanMap = useMemo(
    () => new Map(Object.entries(getSharedSubscriptionPlanContentByType(billingBrandConfig))),
    [billingBrandConfig]
  );
  const roleSubscriptionMapping = useMemo(
    () => BillingConstants.getRoleTypeSubscriptionMapping(billingBrandConfig),
    [billingBrandConfig]
  );
  const planComparisonConfigByRole = useMemo(
    () => getSharedPlanComparisonConfigByRole(billingBrandConfig),
    [billingBrandConfig]
  );
  const localPricingCatalogByRole = useMemo(() => (
    Object.fromEntries(
      Object.keys(BillingConstants.ROLE_SERVICE_TYPE_MAP).map((roleType) => {
        const services = roleSubscriptionMapping.get(roleType)?.services || [];
        const subscriptionTypes = BillingConstants.ROLE_SERVICE_TYPE_MAP[roleType] || [];
        const subscriptionPlans = services.map((service: any, index: number) => {
          const subscriptionType = subscriptionTypes[index];
          const sharedPlanContent = subscriptionType ? sharedSubscriptionPlanMap.get(subscriptionType) : null;
          const sharedPrice = subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME
            ? BillingConstants.getProviderSingleHomeSubscriptionPrice(billingBrandConfig)
            : subscriptionType ? BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[subscriptionType] : null;

          return {
            subscriptionType,
            title: sharedPlanContent?.title || service?.name || "",
            subtitle: sharedPlanContent?.subtitle || "",
            description: sharedPlanContent?.description || "",
            benefits: sharedPlanContent?.benefits || service?.includes || [],
            badgeLabel: sharedPlanContent?.badgeLabel || "Plan",
            isCorePlan: sharedPlanContent?.isCorePlan || false,
            isAddOnPlan: sharedPlanContent?.isAddOnPlan || false,
            price: typeof sharedPrice === "number" ? sharedPrice : service?.price,
          };
        });
        const purchaseTypes = SHARED_ROLE_ONE_TIME_BUNDLE_PURCHASE_TYPES[roleType] || [];
        const oneTimeBundles = SHARED_ONE_TIME_BUNDLE_PACKAGES.filter((bundle) => purchaseTypes.includes(bundle.purchaseType));
        const bundleCategoryOptions = ["assessment", "careplan", "resident", "home"] as const;
        const bundleFilterOptions = [
          { label: "All", value: "all" },
          ...bundleCategoryOptions
            .filter((category) => oneTimeBundles.some((bundle) => (bundle.categories as readonly string[]).includes(category)))
            .map((category) => ({
              label: category === "careplan" ? "Care Plan" : category.charAt(0).toUpperCase() + category.slice(1),
              value: category,
            })),
        ];

        return [roleType, {
          subscriptionPlans,
          oneTimeBundles,
          bundleFilterOptions,
          comparison: planComparisonConfigByRole[roleType] || null,
        }];
      })
    ) as Record<string, any>
  ), [planComparisonConfigByRole, roleSubscriptionMapping, sharedSubscriptionPlanMap]);

  const roleContent = selectedRole
    ? localPricingCatalogByRole[selectedRole]
    : null;
  const normalizedRoleContent = useMemo(() => {
    if (!roleContent) {
      return null;
    }

    return {
      ...roleContent,
      subscriptionPlans: (roleContent.subscriptionPlans || []).map((plan: any) => {
        const sharedPlanContent = sharedSubscriptionPlanMap.get(plan.subscriptionType);
        const sharedPrice = plan.subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME
          ? BillingConstants.getProviderSingleHomeSubscriptionPrice(billingBrandConfig)
          : BillingConstants.SUBSCRIPTION_PRICE_BY_TYPE[plan.subscriptionType];

        return {
          ...plan,
          ...(sharedPlanContent || {}),
          price: typeof sharedPrice === "number" ? sharedPrice : plan.price,
        };
      }),
      oneTimeBundles: (roleContent.oneTimeBundles || []).map((bundle: any) => ({
        ...bundle,
        ...(sharedOneTimeBundleMap.get(bundle.purchaseType) || {}),
      })),
    };
  }, [billingBrandConfig, roleContent, sharedOneTimeBundleMap, sharedSubscriptionPlanMap]);
  const roleTheme = selectedRole ? pricingRoleTheme[selectedRole] || pricingRoleTheme[ROLE_TYPE_PROVIDER] : null;
  const isNurseRole = selectedRole === ROLE_TYPE_NURSE || selectedRole === ROLE_TYPE_MHP;
  const isCaregiverRole = selectedRole === ROLE_TYPE_CAREGIVER;
  const isFacilityWorkerRole = selectedRole === ROLE_TYPE_FACILITY_WORKER;
  const isProviderRole = selectedRole === ROLE_TYPE_PROVIDER;
  const showOneTimeSection = selectedRole !== ROLE_TYPE_CAREGIVER && (normalizedRoleContent?.oneTimeBundles || []).length > 0;
  const providerCorePlan = isProviderRole
    ? (normalizedRoleContent?.subscriptionPlans || []).find((plan: any) => plan.subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME)
    : null;
  const providerAddResidentPlan = isProviderRole
    ? (normalizedRoleContent?.subscriptionPlans || []).find((plan: any) => plan.subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT)
    : null;

  useEffect(() => {
    setActiveSection("subscription");
    setBundleFilter("all");
  }, [selectedRole]);

  useEffect(() => {
    const resolvedRole = resolvePricingRole(searchParams.get("role"), availableRoleTypes);
    setSelectedRole((currentRole) => (currentRole === resolvedRole ? currentRole : resolvedRole));
  }, [availableRoleTypes, searchParams]);

  useEffect(() => {
    const resolvedRole = selectedRole || ROLE_TYPE_PROVIDER;
    const currentRoleParam = String(searchParams.get("role") || "").trim().toLowerCase();
    if (currentRoleParam === resolvedRole) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("role", resolvedRole);
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, selectedRole, setSearchParams]);

  const filteredBundles = (normalizedRoleContent?.oneTimeBundles || []).filter((bundle: any) =>
    bundleFilter === "all" || (bundle.categories || []).includes(bundleFilter)
  );

  return (
    <div style={publicPageContainerStyle}>
      <div>
        <PublicRoleSelector
          selectedRole={selectedRole}
          onChange={setSelectedRole}
          compact
          inline
        />
      </div>

      {isFacilityWorkerRole ? (
        <Card
          className="mt-3"
          style={{
            ...publicSectionCardStyle,
            border: `1px solid ${pricingRoleTheme[ROLE_TYPE_FACILITY_WORKER].border}`,
            background: "linear-gradient(135deg, #f3fbf5 0%, #ffffff 100%)",
          }}
        >
          <div style={{ color: pricingRoleTheme[ROLE_TYPE_FACILITY_WORKER].accent, fontSize: "1.35rem", fontWeight: 700 }}>
            Facility Worker access is free
          </div>
          <p style={{ color: "#36546c", lineHeight: 1.7, marginBottom: 0 }}>
            No subscription is required for the Facility Worker role. Facility Workers view and take action on assigned facility tasks and do not receive resident or clinical access.
          </p>
        </Card>
      ) : null}

      {!normalizedRoleContent ? (
        <div
          className="text-center mt-4"
          style={{
            ...publicSectionCardStyle,
            background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 50%, #eff6ff 100%)",
            border: "1px solid #fed7aa",
          }}
        >
          <h3 style={{ color: "#7c2d12" }}>Select a role to compare pricing</h3>
          <p style={{ color: "#5b4636", maxWidth: "880px", margin: "0.75rem auto 0 auto", lineHeight: 1.8 }}>
            Pricing is different for providers, nurses, staff, family members, and business vendors, so the page stays hidden until you choose the role you want to compare.
          </p>
        </div>
      ) : (
        <>
          {activeSection === "subscription" && (
            <div className="grid mt-3">
              {isNurseRole ? (
                <div className="col-12">
                  <Card
                    className="h-full"
                    style={{
                      ...publicSectionCardStyle,
                      border: `1px solid ${roleTheme.border}`,
                      boxShadow: "0 16px 34px rgba(15, 61, 99, 0.08)",
                      background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                    }}
                  >
                    <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                      <div style={{ maxWidth: "720px" }}>
                        <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0f172a" }}>
                          {BillingConstants.NURSE_PLAN_SERVICE.name}
                        </div>
                        <div className="mt-1" style={{ color: "#64748b", lineHeight: 1.7 }}>
                          Keep nurse billing simple with one monthly plan for everyday workflow.
                        </div>
                      </div>
                      <div
                        className="px-3 py-2 border-round-2xl"
                        style={{
                          backgroundColor: roleTheme.soft,
                          border: `1px solid ${roleTheme.border}`,
                          color: roleTheme.accent,
                          fontWeight: 700,
                        }}
                      >
                        <div>Starting at ${BillingConstants.NURSE_PLAN_SERVICE.price}/month</div>
                        <div className="mt-1" style={{ fontSize: "0.85rem", color: roleTheme.accent, fontWeight: 700 }}>
                          Includes 5 active residents
                        </div>
                      </div>
                    </div>

                    <div className="grid mt-3">
                      <div className="col-12">
                        <div style={sectionTitleStyle}>Included every month</div>
                        <div className="grid">
                          {BillingConstants.NURSE_SERVICE_INCLUDE_SECTIONS
                            .filter((section) => section.title !== "Billing terms")
                            .map((section) => (
                              <div key={section.title} className="col-12 md:col-6">
                                <div
                                  style={{
                                    border: `1px solid ${roleTheme.border}`,
                                    borderRadius: "16px",
                                    padding: "1rem",
                                    background: "#ffffff",
                                    height: "100%",
                                  }}
                                >
                                  <div
                                    style={{
                                      ...sectionLabelStyle,
                                      backgroundColor: roleTheme.soft,
                                      border: `1px solid ${roleTheme.border}`,
                                      color: roleTheme.accent,
                                      marginBottom: "0.75rem",
                                    }}
                                  >
                                    <i className="pi pi-check-circle" style={{ color: roleTheme.chip, fontSize: "0.8rem" }} />
                                    {section.title}
                                  </div>
                                  <ul style={{ ...listStyle, marginTop: 0, marginBottom: 0 }}>
                                    {section.items.map((item) => (
                                      <li key={item}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              ) : isCaregiverRole ? (
                <div className="col-12">
                  <Card
                    className="h-full"
                    style={{
                      ...publicSectionCardStyle,
                      border: `1px solid ${roleTheme.border}`,
                      boxShadow: "0 16px 34px rgba(15, 61, 99, 0.08)",
                      background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                    }}
                  >
                    <div className="grid align-items-start">
                      <div className="col-12 xl:col-6">
                        <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0f172a" }}>
                          Free staff access
                        </div>
                        <div className="mt-1" style={{ color: "#64748b", lineHeight: 1.7 }}>
                          Caregivers use AFH Hub at no cost when a provider or facility adds them to perform assigned resident-care tasks.
                        </div>
                      </div>
                      <div className="col-12 xl:col-6">
                        <div style={{ textAlign: "right" }}>
                          <div
                            className="px-3 py-2 border-round-2xl"
                            style={{
                              display: "inline-block",
                              backgroundColor: roleTheme.soft,
                              border: `1px solid ${roleTheme.border}`,
                              color: roleTheme.accent,
                              fontWeight: 700,
                            }}
                          >
                            <div style={{ fontSize: "1.05rem", fontWeight: 800 }}>
                              Free
                            </div>
                            <div className="mt-1" style={{ fontSize: "0.85rem", color: roleTheme.accent, fontWeight: 700 }}>
                              When caregiver added by a provider or facility
                            </div>
                          </div>
                          <div className="mt-1" style={{ color: "#64748b", fontSize: "0.86rem", lineHeight: 1.5 }}>
                            Access starts after your assignment and permissions are set.
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div style={{ ...sectionTitleStyle, marginBottom: "1rem" }}>Available with assigned access</div>
                      <div className="grid">
                        {[
                          {
                            title: "Care Planning",
                            items: [
                              "Search care plan details and resident instructions.",
                              "Review support guidance and task-specific care notes.",
                            ],
                          },
                          {
                            title: "Daily Documentation",
                            items: [
                              "Create daily notes for routine shift updates and observations.",
                              "Chart ADL tasks with support levels, tracking parameters, and daily reports.",
                              "Submit incident reports and follow-up documentation.",
                            ],
                          },
                          {
                            title: "Behavior Tracking",
                            items: [
                              "Record daily behavior events and interventions used.",
                              "Support CBHS / IBSS monthly reporting workflows.",
                            ],
                          },
                          {
                            title: "Medication Workflow",
                            items: [
                              "Review medication lists for assigned residents.",
                              "Chart eMAR activity based on staff access permissions.",
                            ],
                          },
                        ].map((section) => (
                          <div key={section.title} className="col-12 md:col-6 xl:col-3">
                            <div
                              style={{
                                border: `1px solid ${roleTheme.border}`,
                                borderRadius: "16px",
                                padding: "1rem",
                                background: "#ffffff",
                                height: "100%",
                              }}
                            >
                              <div
                                style={{
                                  ...sectionLabelStyle,
                                  backgroundColor: roleTheme.soft,
                                  border: `1px solid ${roleTheme.border}`,
                                  color: roleTheme.accent,
                                  marginBottom: "0.75rem",
                                }}
                              >
                                <i
                                  className="pi pi-check-circle"
                                  style={{ color: roleTheme.chip, fontSize: "0.8rem" }}
                                />
                                {section.title}
                              </div>
                              <ul style={{ ...listStyle, marginTop: 0, marginBottom: 0 }}>
                                {section.items.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                </div>
              ) : isProviderRole && providerCorePlan ? (
                <div className="col-12">
                  <Card
                    className="h-full"
                    style={{
                      ...publicSectionCardStyle,
                      border: `1px solid ${roleTheme.border}`,
                      boxShadow: "0 16px 34px rgba(15, 61, 99, 0.08)",
                      background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                    }}
                  >
                    <div className="grid align-items-start">
                      <div className="col-12 xl:col-6">
                        <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0f172a" }}>
                          Monthly provider workspace access
                        </div>
                        <div className="mt-1" style={{ color: "#64748b", lineHeight: 1.7 }}>
                          Manage resident assessments, care planning, eMAR, and CBHS/IBSS tracking in one monthly provider workspace with your first residents already included.
                        </div>
                      </div>
                      <div className="col-12 xl:col-6">
                        <div style={{ textAlign: "right" }}>
                          <div
                            className="px-3 py-2 border-round-2xl"
                            style={{
                              display: "inline-block",
                              backgroundColor: "#fff7ed",
                              border: "1px solid #fdba74",
                              color: "#9a3412",
                              fontWeight: 700,
                            }}
                          >
                            <div style={{ fontSize: "1.05rem", fontWeight: 800 }}>
                              ${providerCorePlan.price}/month
                            </div>
                            <div className="mt-1" style={{ fontSize: "0.85rem", color: "#9a3412", fontWeight: 700 }}>
                              Includes {BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME} residents and {BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} staff
                            </div>
                          </div>
                          {providerAddResidentPlan ? (
                            <div className="mt-1" style={{ color: "#64748b", fontSize: "0.86rem", lineHeight: 1.5 }}>
                              +$5/month for each extra resident or each extra staff member
                            </div>
                          ) : null}
                          <div className="mt-1" style={{ color: "#64748b", fontSize: "0.86rem", lineHeight: 1.5 }}>
                            Staff means caregivers and RNs you work with.
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div style={{ ...sectionTitleStyle, marginBottom: "1rem" }}>Included every month</div>
                      <div className="grid">
                        {BillingConstants.PROVIDER_SERVICE_INCLUDE_SECTIONS.map((section) => (
                          <div key={section.title} className="col-12 md:col-6 xl:col-3">
                            <div
                              style={{
                                border: `1px solid ${roleTheme.border}`,
                                borderRadius: "16px",
                                padding: "1rem",
                                background: "#ffffff",
                                height: "100%",
                              }}
                            >
                              <div
                                style={{
                                  ...sectionLabelStyle,
                                  backgroundColor: roleTheme.soft,
                                  border: `1px solid ${roleTheme.border}`,
                                  color: roleTheme.accent,
                                  marginBottom: "0.75rem",
                                }}
                              >
                                <i
                                  className="pi pi-check-circle"
                                  style={{ color: roleTheme.chip, fontSize: "0.8rem" }}
                                />
                                {section.title}
                              </div>
                              <ul style={{ ...listStyle, marginTop: 0, marginBottom: 0 }}>
                                {section.items.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                </div>
              ) : normalizedRoleContent.subscriptionPlans.map((plan: any) => (
                <div key={plan.title} className="col-12 md:col-6">
                  <Card
                    className="h-full"
                    style={{
                      ...publicSectionCardStyle,
                      border: `1px solid ${roleTheme.border}`,
                      boxShadow: "0 16px 34px rgba(15, 61, 99, 0.08)",
                      background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                    }}
                  >
                    <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                      <div>
                        <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#0f172a" }}>{plan.title}</div>
                        <div className="mt-1" style={{ color: "#64748b" }}>{plan.subtitle}</div>
                      </div>
                      <div
                        className="px-3 py-2 border-round-2xl"
                        style={{
                          backgroundColor: roleTheme.soft,
                          border: `1px solid ${roleTheme.border}`,
                          color: roleTheme.accent,
                          fontWeight: 700,
                        }}
                      >
                        ${plan.price}/month
                      </div>
                    </div>

                    <div className="mt-4" style={sectionTitleStyle}>What you get every month</div>
                    <ul style={listStyle} className="mt-0 mb-0">
                      {plan.benefits.map((benefit: string) => (
                        <li key={benefit}>{benefit}</li>
                      ))}
                    </ul>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {activeSection === "comparison" && (
            <Card
              className="mt-4"
              style={{
                ...publicSectionCardStyle,
                background: "linear-gradient(135deg, #fffaf0 0%, #ffffff 100%)",
                border: "1px solid #fde68a",
              }}
            >
              <div style={{ maxWidth: "980px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", maxWidth: "760px", margin: "0 auto" }}>
                  <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "#7c2d12" }}>{normalizedRoleContent.comparison.title}</div>
                  <div className="mt-2" style={{ color: "#7c2d12", lineHeight: 1.8 }}>
                    {normalizedRoleContent.comparison.summary}
                  </div>
                </div>
                <div className="grid justify-content-center align-items-start mt-3">
                  <div className="col-12 lg:col-6">
                    <div style={{ background: "#ffffff", border: "1px solid #fed7aa", borderRadius: "14px", padding: "1rem", height: "100%" }}>
                      <div style={{ fontWeight: 700, color: "#9a3412" }}>{normalizedRoleContent.comparison.leftTitle}</div>
                      <div className="mt-2" style={{ color: "#64748b", lineHeight: 1.7 }}>
                        {normalizedRoleContent.comparison.leftDescription}
                      </div>
                      <ul style={{ ...listStyle, marginTop: "1rem", marginBottom: 0 }}>
                        {normalizedRoleContent.comparison.leftBullets.map((bullet: string) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="col-12 lg:col-6">
                    <div style={{ background: "#ffffff", border: "1px solid #dbeafe", borderRadius: "14px", padding: "1rem", height: "100%" }}>
                      <div style={{ fontWeight: 700, color: "#1d4ed8" }}>{normalizedRoleContent.comparison.rightTitle}</div>
                      <div className="mt-2" style={{ color: "#64748b", lineHeight: 1.7 }}>
                        {normalizedRoleContent.comparison.rightDescription}
                      </div>
                      <div className="mt-3" style={{ border: "1px solid #e2e8f0", borderRadius: "14px", overflow: "hidden", background: "#ffffff" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ background: "#f8fafc", color: "#334155" }}>
                              <th style={{ textAlign: "left", padding: "0.75rem 0.9rem", fontWeight: 700 }}>{normalizedRoleContent.comparison.columns[0]}</th>
                              <th style={{ textAlign: "right", padding: "0.75rem 0.9rem", fontWeight: 700 }}>{normalizedRoleContent.comparison.columns[1]}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {normalizedRoleContent.comparison.rows.map((row: any) => (
                              <tr key={row.label} style={{ borderTop: "1px solid #e2e8f0" }}>
                                <td style={{ padding: "0.75rem 0.9rem", color: "#475569" }}>{row.label}</td>
                                <td style={{ padding: "0.75rem 0.9rem", color: "#0f172a", textAlign: "right" }}>{row.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4" style={{ background: "#ecfdf5", border: "1px solid #86efac", borderRadius: "14px", padding: "1rem", color: "#166534", lineHeight: 1.8, textAlign: "center", maxWidth: "760px", marginLeft: "auto", marginRight: "auto" }}>
                  {normalizedRoleContent.comparison.footer}
                </div>
              </div>
            </Card>
          )}

          {activeSection === "oneTime" && showOneTimeSection && (
            <>
              <div className="mt-4">
                <div style={{ ...sectionTitleStyle, fontSize: "1.15rem" }}>{isProviderRole ? "One-Time Options" : "One-Time Option"}</div>
                <div className="mt-2" style={{ color: "#9a3412", fontWeight: 700, lineHeight: 1.6 }}>
                  For subscribed users only.
                </div>
                {isNurseRole && (
                  <div className="mt-2" style={{ color: "#64748b", lineHeight: 1.7, maxWidth: "860px" }}>
                    Review one-time purchase options for occasional extra runs without changing your monthly plan.
                  </div>
                )}
                {(normalizedRoleContent.bundleFilterOptions || []).length > 1 ? (
                  <>
                    {/* <div className="mt-3 flex flex-wrap justify-content-center align-items-center gap-2">
                      {normalizedRoleContent.bundleFilterOptions.map((option: any) => (
                      <Tag
                        key={option.value}
                        value={option.label}
                        severity={bundleFilter === option.value ? "warning" : "secondary"}
                        style={{
                          borderRadius: "999px",
                          padding: "0.45rem 0.9rem",
                          fontSize: "0.9rem",
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1.2,
                        }}
                        onClick={() => setBundleFilter(option.value)}
                      />
                    ))}
                  </div> */}
                  </>
                ) : null}
              </div>

              <div className="grid mt-1">
                {isNurseRole && (
                  <div className="col-12 md:col-6">
                    <Card
                      className="h-full"
                      style={{
                        ...publicSectionCardStyle,
                        border: `1px solid ${roleTheme.border}`,
                        boxShadow: "0 16px 34px rgba(15, 61, 99, 0.08)",
                        background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                      }}
                    >
                      <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                        <div style={{ maxWidth: "720px" }}>
                          <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#0f172a" }}>Increase monthly automation</div>
                          <div className="mt-1" style={{ color: "#596F80", lineHeight: 1.7 }}>
                            Best when you need more automation every month.
                          </div>
                        </div>
                        <div
                          className="px-3 py-2 border-round-2xl"
                          style={{
                            backgroundColor: roleTheme.soft,
                            border: `1px solid ${roleTheme.border}`,
                            color: roleTheme.accent,
                            fontWeight: 700,
                          }}
                        >
                          <div>+${BillingConstants.NURSE_AI_ASSESSMENT_BLOCK_PRICE}/month</div>
                          <div style={{ marginTop: "0.2rem", fontSize: "0.8rem", fontWeight: 800, lineHeight: 1.3 }}>
                            {BillingConstants.NURSE_AI_ASSESSMENT_BLOCK_SIZE} extra monthly credits
                          </div>
                        </div>
                      </div>
                      <div
                        className="mt-4"
                        style={{
                          border: `1px solid ${roleTheme.border}`,
                          borderRadius: "16px",
                          padding: "1rem",
                          background: "#ffffff",
                        }}
                      >
                        <div style={{ ...sectionTitleStyle, marginBottom: "0.65rem" }}>Best for recurring work</div>
                        <ul style={{ ...listStyle, marginTop: 0, marginBottom: 0 }}>
                          <li>Adds monthly automation capacity to the subscription.</li>
                          <li>Useful for nurses with steady repeated assessment or NCP volume.</li>
                          <li>Keep one-time credits for occasional overflow instead.</li>
                        </ul>
                      </div>
                    </Card>
                  </div>
                )}
                {filteredBundles.length > 0 ? filteredBundles.map((bundle: any) => (
                  <div key={bundle.name} className="col-12 md:col-6">
                    {(() => {
                      const isHighlightedBundle = bundle.purchaseType === "nurse_automation_pack";
                      const primaryIncludedLabel = isHighlightedBundle ? bundle.includes?.[0] : null;

                      return (
                        <Card
                          className="h-full"
                          style={{
                            ...publicSectionCardStyle,
                            border: isHighlightedBundle ? "2px solid #f59e0b" : `1px solid ${roleTheme.border}`,
                            boxShadow: isHighlightedBundle
                              ? "0 20px 42px rgba(245, 158, 11, 0.18)"
                              : "0 16px 34px rgba(15, 61, 99, 0.08)",
                            background: isHighlightedBundle
                              ? "linear-gradient(135deg, #fff8eb 0%, #ffffff 100%)"
                              : "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          {isHighlightedBundle && (
                            <div
                              style={{
                                position: "absolute",
                                top: "1rem",
                                right: "1rem",
                                borderRadius: "999px",
                                padding: "0.32rem 0.75rem",
                                background: "#f59e0b",
                                color: "#ffffff",
                                fontSize: "0.72rem",
                                fontWeight: 800,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                              }}
                            >
                              Featured
                            </div>
                          )}
                          <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                            <div style={{ maxWidth: "720px" }}>
                              <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#0f172a" }}>{bundle.name}</div>
                              <div className="mt-1" style={{ color: "#596F80", lineHeight: 1.7 }}>
                                {bundle.subtitle}
                              </div>
                            </div>
                            <div
                              className="px-3 py-2 border-round-2xl"
                              style={{
                                backgroundColor: isHighlightedBundle ? "#fff1cc" : roleTheme.soft,
                                border: isHighlightedBundle ? "1px solid #f5b942" : `1px solid ${roleTheme.border}`,
                                color: isHighlightedBundle ? "#b45309" : bundle.accentColor,
                                fontWeight: 700,
                              }}
                            >
                              <div>${bundle.price}</div>
                              {primaryIncludedLabel && (
                                <div
                                  style={{
                                    marginTop: "0.2rem",
                                    fontSize: "0.8rem",
                                    fontWeight: 800,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {primaryIncludedLabel}
                                </div>
                              )}
                            </div>
                          </div>
                          <div
                            className="mt-4"
                            style={{
                              border: isHighlightedBundle ? "1px solid #f8d38a" : `1px solid ${roleTheme.border}`,
                              borderRadius: "16px",
                              padding: "1rem",
                              background: isHighlightedBundle ? "#fffdf8" : "#ffffff",
                            }}
                          >
                            <div style={{ ...sectionTitleStyle, marginBottom: "0.65rem" }}>Included with this purchase</div>
                            <ul style={{ ...listStyle, marginTop: 0, marginBottom: 0 }}>
                              {bundle.includes
                                .filter((item: string) => item !== primaryIncludedLabel)
                                .map((item: string) => (
                                  <li key={item}>{item}</li>
                                ))}
                            </ul>
                          </div>
                        </Card>
                      );
                    })()}
                  </div>
                )) : (
                  <div className="col-12">
                    <Card className="h-full" style={publicSectionCardStyle}>
                      <div style={{ color: "#0f3d63", fontWeight: 700 }}>No one-time options for this role</div>
                      <div className="mt-2" style={{ color: "#36546c", lineHeight: 1.8 }}>
                        This role is designed around a straightforward monthly subscription instead of extra one-time purchases.
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Pricing;
